import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import type { DefaultExtensionProvider, Parameters } from '@atlaskit/editor-common/extensions';
import type { EditorInjectionAPI } from '@atlaskit/editor-common/types';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import { ComposableEditor, type EditorNextProps } from '@atlaskit/editor-core/composable-editor';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import type { Skill } from '@atlassian/rovo-platform-types/skills';
import { useRovoSkillsInvalidation } from '@atlassian/rovo-skills/services/rovo-skills-config-provider';
import { parseUniversalSkillEdges } from '@atlassian/rovo-skills/utils/parse-skill';

import { useSkillsManifest } from '../../../ui/skills-editor-extension/useSkillsManifest';
import { useSkillTagExtensionProvider } from '../../../ui/skills-editor-extension/useSkillTagExtensionProvider';

import type { composableEditorChat_GetAvailableSkillsQuery } from './__generated__/composableEditorChat_GetAvailableSkillsQuery.graphql';

// Check if we're at 90% of the API limit (150 items)
const API_LIMIT = 150;
const PAGINATION_THRESHOLD = API_LIMIT * 0.9; // 90 items

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EditorApiWithTypeAhead = EditorInjectionAPI<any, any> | null | undefined;

type ComposableEditorWithSkillsProps = EditorNextProps & {
	siteId: string;
	promptHasSkill: boolean;
	isSpecialistPromptInput: boolean;
	enableSkills?: boolean;
	editorApi?: EditorApiWithTypeAhead;
	disableSkillTagHoverCardInEditor?: boolean;
	skillTagHoverCardMaxHeightInEditor?: number | string;
};

type SkillsResponse = NonNullable<
	composableEditorChat_GetAvailableSkillsQuery['response']
>['convoai_availableSkills'];

type SkillsFetcherProps = Pick<
	ComposableEditorWithSkillsProps,
	| 'isSpecialistPromptInput'
	| 'promptHasSkill'
	| 'enableSkills'
	| 'siteId'
	| 'disableSkillTagHoverCardInEditor'
	| 'skillTagHoverCardMaxHeightInEditor'
> & {
	skillsRevision: number;
	onManifestReady: (manifest: DefaultExtensionProvider<Skill>[] | undefined) => void;
};

const ComposableEditorSkillsUnified = ({
	isSpecialistPromptInput,
	promptHasSkill,
	enableSkills = true,
	siteId,
	disableSkillTagHoverCardInEditor,
	skillTagHoverCardMaxHeightInEditor,
	skillsRevision,
	onManifestReady,
}: SkillsFetcherProps): JSX.Element | null => {
	const skillsResponse = useLazyLoadQuery<composableEditorChat_GetAvailableSkillsQuery>(
		/* eslint-disable @atlassian/relay/unused-fields -- fields consumed by parseUniversalSkillEdges and kept for future use */
		graphql`
			query composableEditorChat_GetAvailableSkillsQuery(
				$first: Int
				$after: String
				$cloudId: ID!
				$shouldFetch: Boolean = false
			) {
				convoai_availableSkills(first: $first, after: $after, cloudId: $cloudId)
					@optIn(to: "ConvoAiAvailableSkills")
					@connection(key: "Skills__convoai_availableSkills")
					@include(if: $shouldFetch) {
					edges {
						node {
							__typename
							id
							name
							description
							displayName
							slashCommand
							iconUrl
							iconKey
							helpText
							color
							source
							tags
							category
							integrationKeys
							integrations {
								key
								id
								displayName
								iconUrl
							}
							version
						}
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						startCursor
						endCursor
					}
					totalCount
					metadata {
						anthropicCount
						stratusCount
						isDegraded
					}
					errors {
						message
					}
				}
			}
		`,
		/* eslint-enable @atlassian/relay/unused-fields */
		{
			first: API_LIMIT,
			cloudId: siteId,
			shouldFetch: enableSkills,
		},
		{
			fetchKey: skillsRevision,
			fetchPolicy: skillsRevision === 0 ? 'store-or-network' : 'network-only',
		},
	)?.convoai_availableSkills;

	return (
		<SkillsManifestReporter
			skillsResponse={skillsResponse}
			isSpecialistPromptInput={isSpecialistPromptInput}
			promptHasSkill={promptHasSkill}
			enableSkills={enableSkills}
			siteId={siteId}
			disableSkillTagHoverCardInEditor={disableSkillTagHoverCardInEditor}
			skillTagHoverCardMaxHeightInEditor={skillTagHoverCardMaxHeightInEditor}
			skillsRevision={skillsRevision}
			onManifestReady={onManifestReady}
		/>
	);
};

const SkillsManifestReporter = ({
	skillsResponse,
	isSpecialistPromptInput,
	promptHasSkill,
	enableSkills,
	siteId,
	disableSkillTagHoverCardInEditor,
	skillTagHoverCardMaxHeightInEditor,
	skillsRevision,
	onManifestReady,
}: SkillsFetcherProps & {
	skillsResponse: SkillsResponse;
}): null => {
	const skillsPaginationRef = useRef<boolean>(false);

	if (skillsResponse?.errors && skillsResponse.errors.length > 0) {
		const errorMessages = skillsResponse.errors.map((err) => err.message).join(', ');
		throw new Error(errorMessages || 'Failed to load skills');
	}

	const skillCount = skillsResponse?.edges?.length ?? 0;

	if (skillCount >= PAGINATION_THRESHOLD && !skillsPaginationRef.current) {
		logException({
			exception: new Error(
				`Skills API limit threshold reached: ${skillCount} of ${API_LIMIT} items returned. Pagination is needed.`,
			),
			name: 'aiMateBrowseSkillsPaginationLimit',
			tags: {
				skillCount: skillCount.toString(),
				apiLimit: API_LIMIT.toString(),
			},
		});
		// prevent more than one log per render when threshold is reached
		skillsPaginationRef.current = true;
	}

	const skillsListAferLoad: Skill[] = React.useMemo(
		() => parseUniversalSkillEdges(skillsResponse?.edges ?? null),
		[skillsResponse?.edges],
	);

	const editorExtensionManifest = useSkillsManifest({
		skills: skillsListAferLoad,
		isSpecialistPromptInput,
		promptHasSkill,
		enableSkills,
		siteId,
		disableSkillTagHoverCardInEditor,
		skillTagHoverCardMaxHeightInEditor,
	});

	const prevInputsRef = useRef<string | null>(null);
	const inputsKey = `${skillsRevision}-${skillsListAferLoad.length}-${isSpecialistPromptInput}-${promptHasSkill}-${enableSkills}-${siteId}-${disableSkillTagHoverCardInEditor}-${skillTagHoverCardMaxHeightInEditor}`;

	useEffect(() => {
		if (editorExtensionManifest === undefined || prevInputsRef.current === inputsKey) {
			return;
		}
		prevInputsRef.current = inputsKey;
		onManifestReady(editorExtensionManifest);
	}, [editorExtensionManifest, inputsKey, onManifestReady]);

	return null;
};

const SkillsDataFetcher = (props: SkillsFetcherProps): JSX.Element | null => {
	return <ComposableEditorSkillsUnified {...props} />;
};

export const ComposableEditorWithSkills = ({
	isSpecialistPromptInput,
	promptHasSkill,
	enableSkills = true,
	siteId,
	editorApi,
	disableSkillTagHoverCardInEditor,
	skillTagHoverCardMaxHeightInEditor,
	...props
}: ComposableEditorWithSkillsProps): JSX.Element => {
	const { skillsRevision } = useRovoSkillsInvalidation();
	const skillTagFallbackProvider = useSkillTagExtensionProvider(
		siteId,
		disableSkillTagHoverCardInEditor,
		skillTagHoverCardMaxHeightInEditor,
	);
	const fallbackExtensionProviders = enableSkills ? skillTagFallbackProvider : undefined;
	const [extensionProviders, setExtensionProviders] = useState<
		DefaultExtensionProvider<Skill>[] | DefaultExtensionProvider<Parameters>[] | undefined
	>(fallbackExtensionProviders);

	const onManifestReady = useCallback((manifest: DefaultExtensionProvider<Skill>[] | undefined) => {
		setExtensionProviders(manifest);
	}, []);

	const { sendAnalyticsEvent } = useAnalytics();
	const typeAheadWasOpenRef = useRef(false);

	const onTypeAheadPluginState = useCallback(
		({ typeAheadState }: { typeAheadState?: { isOpen?: boolean } }) => {
			const isOpen = Boolean(typeAheadState?.isOpen);
			if (isOpen && !typeAheadWasOpenRef.current) {
				sendAnalyticsEvent({
					eventType: 'screen',
					name: 'rovoChatEditorTypeAhead',
				});
			}
			typeAheadWasOpenRef.current = isOpen;
		},
		[sendAnalyticsEvent],
	);

	usePluginStateEffect(editorApi, ['typeAhead'], onTypeAheadPluginState, {
		disabled: !editorApi,
	});

	return (
		<>
			<Suspense fallback={null}>
				<SkillsDataFetcher
					enableSkills={enableSkills}
					siteId={siteId}
					isSpecialistPromptInput={isSpecialistPromptInput}
					promptHasSkill={promptHasSkill}
					disableSkillTagHoverCardInEditor={disableSkillTagHoverCardInEditor}
					skillTagHoverCardMaxHeightInEditor={skillTagHoverCardMaxHeightInEditor}
					skillsRevision={skillsRevision}
					onManifestReady={onManifestReady}
				/>
			</Suspense>
			<ComposableEditor extensionProviders={extensionProviders} {...props} />
		</>
	);
};
